/**
 * Centralized wizard step validation.
 * Returns an error message string or null if valid.
 */

import type { IndividualFormData } from "../types/individual.types";
import type { BusinessFormData, BusinessDetails } from "../types/business.types";
import { BUSINESS_TYPE } from "../types/wizard.types";

function nonEmpty(s: string | undefined | null): boolean {
  return typeof s === "string" && s.trim() !== "";
}

function nonEmptyNum(n: number | undefined | null): boolean {
  return typeof n === "number" && !Number.isNaN(n);
}

/** Validate Individual step by index. Step 0 = Personal, 1 = Residency, etc. */
export function validateIndividualStep(
  stepIndex: number,
  data: IndividualFormData
): string | null {
  if (stepIndex === 0) {
    const list = data.personalInformation ?? [];
    const taxpayer = list.find((p) => p.personType === "TAXPAYER");
    const spouse = list.find((p) => p.personType === "SPOUSE");
    const addr = data.addressInformation ?? {};
    const hasSpouse = !!spouse;

    if (!taxpayer) return "Taxpayer information is required.";
    if (!nonEmpty(taxpayer.firstName)) return "Taxpayer first name is required.";
    if (!nonEmpty(taxpayer.lastName)) return "Taxpayer last name is required.";
    if (!nonEmpty(taxpayer.dateOfBirth)) return "Date of birth is required.";
    if (!nonEmpty(taxpayer.immigrationDetails?.visaType)) return "Type of visa is required.";
    if (!nonEmpty(taxpayer.occupation)) return "Occupation is required.";
    if (!nonEmpty(data.filingStatus as string)) return "Marital / filing status is required.";
    if (!nonEmpty(addr.currentAddress)) return "Street address is required.";
    if (!nonEmpty(addr.city)) return "City is required.";
    if (!nonEmpty(addr.state as string)) return "State is required.";
    if (!nonEmpty(addr.zip)) return "ZIP is required.";

    if (hasSpouse && spouse) {
      if (!nonEmpty(spouse.firstName)) return "Spouse first name is required.";
      if (!nonEmpty(spouse.lastName)) return "Spouse last name is required.";
      if (!nonEmpty(spouse.dateOfBirth)) return "Spouse date of birth is required.";
    }

    return null;
  }

  if (stepIndex === 1) {
    const list = data.residencyDetails ?? [];
    const taxpayerRes = list.filter((r) => r.personType === "TAXPAYER");
    const hasSpouse = (data.personalInformation ?? []).some((p) => p.personType === "SPOUSE");
    const spouseRes = list.filter((r) => r.personType === "SPOUSE");

    const taxpayerOk =
      taxpayerRes.length > 0 &&
      taxpayerRes.some((r) => nonEmpty(r.state as string) && (r.startDate != null || r.endDate != null || r.year != null));
    if (!taxpayerOk)
      return "At least one state of residence with state and dates is required for the taxpayer.";

    if (hasSpouse) {
      const spouseOk =
        spouseRes.length > 0 &&
        spouseRes.some((r) => nonEmpty(r.state as string) && (r.startDate != null || r.endDate != null || r.year != null));
      if (!spouseOk)
        return "At least one state of residence with state and dates is required for the spouse.";
    }

    return null;
  }

  return null;
}

const PARTNERSHIP_TYPES = [BUSINESS_TYPE.PARTNERSHIP, BUSINESS_TYPE.S_CORP];

/** Validate Business step by index. Step 0 = Business Details, etc. */
export function validateBusinessStep(
  stepIndex: number,
  data: BusinessFormData
): string | null {
  if (stepIndex !== 0) return null;

  const d: Partial<BusinessDetails> = data.businessDetails ?? {};
  const addr = d.businessAddress ?? {};
  const partnership = data.partnershipDetails ?? {};
  const partners = partnership.partnerInformation ?? [];
  const numPartners = partnership.numberOfPartners ?? 0;
  const isPartnership = d.businessType != null && PARTNERSHIP_TYPES.includes(d.businessType as (typeof PARTNERSHIP_TYPES)[number]);

  if (!nonEmpty(d.businessType)) return "Type of business is required.";
  if (!nonEmpty(d.proprietorName)) return "Name of the business owner is required.";
  if (!nonEmpty(d.principalBusiness)) return "Principal business or profession is required.";
  if (!nonEmpty(d.businessName)) return "Business name is required.";
  if (!nonEmpty(addr.street)) return "Business street address is required.";
  if (!nonEmpty(addr.city)) return "Business city is required.";
  if (!nonEmpty(addr.state as string)) return "Business state is required.";
  if (!nonEmpty(addr.zip)) return "Business ZIP is required.";
  if (!d.accountingMethod) return "Accounting method is required.";

  if (isPartnership) {
    if (!nonEmptyNum(numPartners) || numPartners < 1)
      return "Number of partners is required (at least 1).";
    const slice = partners.slice(0, numPartners);
    for (let i = 0; i < slice.length; i++) {
      const p = slice[i] ?? {};
      if (!nonEmpty(p.name)) return `Partner ${i + 1}: Name is required.`;
      const hasAddress =
        nonEmpty(p.street ?? p.address) && nonEmpty(p.city) && nonEmpty(p.state as string) && nonEmpty(p.zip);
      if (!hasAddress) return `Partner ${i + 1}: Full address (street, city, state, ZIP) is required.`;
      if (!nonEmptyNum(p.partnershipPercentage) || (p.partnershipPercentage as number) < 0)
        return `Partner ${i + 1}: Partnership percentage is required.`;
    }
  }

  return null;
}
